import React, { useState, useEffect } from 'react';
import { adminsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { 
  Shield, 
  Plus, 
  Trash2, 
  Edit, 
  Crown,
  User,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

export default function Admins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    telegram_id: '',
    username: '',
    first_name: '',
    is_super_admin: false,
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminsApi.getAll();
      setAdmins(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки администраторов');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      if (!formData.telegram_id) {
        toast.error('Введите Telegram ID');
        return;
      }
      
      await adminsApi.create(formData);
      toast.success('Администратор добавлен');
      setIsAddOpen(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Ошибка добавления администратора');
      }
    }
  };

  const handleEdit = async () => {
    try {
      await adminsApi.update(selectedAdmin.id, {
        username: formData.username,
        first_name: formData.first_name,
        is_super_admin: formData.is_super_admin,
        is_active: formData.is_active,
      });
      toast.success('Администратор обновлен');
      setIsEditOpen(false);
      resetForm();
      fetchAdmins();
    } catch (error) {
      toast.error('Ошибка обновления администратора');
    }
  };

  const handleDelete = async () => {
    try {
      await adminsApi.delete(selectedAdmin.id);
      toast.success('Администратор удален');
      setIsDeleteOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error('Ошибка удаления администратора');
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await adminsApi.update(admin.id, { is_active: !admin.is_active });
      toast.success(admin.is_active ? 'Администратор деактивирован' : 'Администратор активирован');
      fetchAdmins();
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      telegram_id: admin.telegram_id,
      username: admin.username || '',
      first_name: admin.first_name || '',
      is_super_admin: admin.is_super_admin,
      is_active: admin.is_active,
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      telegram_id: '',
      username: '',
      first_name: '',
      is_super_admin: false,
    });
    setSelectedAdmin(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#2AABEE]" />
            Администраторы бота
          </h1>
          <p className="text-zinc-500 mt-1">
            Управление пользователями с доступом к админ-панели через Telegram бот
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAdmins}
            className="border-zinc-800 hover:bg-zinc-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Обновить
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setIsAddOpen(true);
            }}
            className="bg-[#2AABEE] hover:bg-[#229ED9]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-[#2AABEE]/10 border border-[#2AABEE]/20 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-[#2AABEE] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-[#2AABEE] font-medium mb-1">Как это работает?</p>
            <p className="text-zinc-400">
              Администраторы могут управлять ботом через команду <code className="bg-zinc-800 px-1 rounded">/admin</code> в Telegram. 
              Первый пользователь, отправивший эту команду, автоматически становится главным администратором.
              Главный администратор (👑) может добавлять и удалять других админов.
            </p>
          </div>
        </div>
      </div>

      {/* Admins List */}
      {loading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-[#0a0a0a] rounded-xl border border-white/5 p-6 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-zinc-800" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-zinc-800 rounded w-1/3" />
                  <div className="h-3 bg-zinc-800 rounded w-1/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : admins.length === 0 ? (
        <div className="bg-[#0a0a0a] rounded-xl border border-white/5 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-zinc-600" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">Нет администраторов</h3>
          <p className="text-zinc-500 mb-6 max-w-md mx-auto">
            Первый пользователь, который отправит команду /admin боту, станет главным администратором
          </p>
          <Button
            onClick={() => {
              resetForm();
              setIsAddOpen(true);
            }}
            className="bg-[#2AABEE] hover:bg-[#229ED9]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить вручную
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {admins.map((admin) => (
            <div
              key={admin.id}
              className={`bg-[#0a0a0a] rounded-xl border p-6 transition-colors ${
                admin.is_active ? 'border-white/5 hover:border-white/10' : 'border-red-900/30 bg-red-950/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    admin.is_super_admin ? 'bg-amber-500/20' : 'bg-[#2AABEE]/20'
                  }`}>
                    {admin.is_super_admin ? (
                      <Crown className="w-6 h-6 text-amber-500" />
                    ) : (
                      <User className="w-6 h-6 text-[#2AABEE]" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">
                        {admin.username ? `@${admin.username}` : admin.first_name || `ID: ${admin.telegram_id}`}
                      </h3>
                      {admin.is_super_admin && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-500 rounded-full">
                          Главный
                        </span>
                      )}
                      {!admin.is_active && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                          Неактивен
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 mt-1">
                      Telegram ID: <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-400">{admin.telegram_id}</code>
                    </p>
                    <p className="text-xs text-zinc-600 mt-1">
                      Добавлен: {new Date(admin.created_at).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={admin.is_active}
                    onCheckedChange={() => handleToggleActive(admin)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(admin)}
                    className="hover:bg-zinc-800"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedAdmin(admin);
                      setIsDeleteOpen(true);
                    }}
                    className="hover:bg-red-900/30 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Добавить администратора</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Добавьте пользователя Telegram как администратора бота
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="telegram_id" className="text-zinc-300">Telegram ID *</Label>
              <Input
                id="telegram_id"
                placeholder="123456789"
                value={formData.telegram_id}
                onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
              <p className="text-xs text-zinc-500">
                Пользователь может узнать свой ID через бота @userinfobot
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-300">Username</Label>
              <Input
                id="username"
                placeholder="username (без @)"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="first_name" className="text-zinc-300">Имя</Label>
              <Input
                id="first_name"
                placeholder="Имя администратора"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-zinc-300">Главный администратор</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Может управлять другими админами
                </p>
              </div>
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)} className="border-zinc-800">
              Отмена
            </Button>
            <Button onClick={handleAdd} className="bg-[#2AABEE] hover:bg-[#229ED9]">
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Редактировать администратора</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Telegram ID</Label>
              <Input
                value={formData.telegram_id}
                disabled
                className="bg-zinc-900 border-zinc-800 opacity-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_username" className="text-zinc-300">Username</Label>
              <Input
                id="edit_username"
                placeholder="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_first_name" className="text-zinc-300">Имя</Label>
              <Input
                id="edit_first_name"
                placeholder="Имя администратора"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-zinc-300">Главный администратор</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Может управлять другими админами
                </p>
              </div>
              <Switch
                checked={formData.is_super_admin}
                onCheckedChange={(checked) => setFormData({ ...formData, is_super_admin: checked })}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-zinc-300">Активен</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Неактивные админы не имеют доступа
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)} className="border-zinc-800">
              Отмена
            </Button>
            <Button onClick={handleEdit} className="bg-[#2AABEE] hover:bg-[#229ED9]">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Удалить администратора?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-500">
              {selectedAdmin && (
                <>
                  Администратор {selectedAdmin.username ? `@${selectedAdmin.username}` : `ID: ${selectedAdmin.telegram_id}`} потеряет доступ к админ-панели бота.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-800 hover:bg-zinc-800">Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
